import { apiClient, apiClientPaginated, apiClientEnvelope } from '@/lib/api-client';
import type {
  ProductSerial,
  SerialStatus,
  Product,
  CreateProductPayload,
  UpdateProductPayload,
  StockAdjustmentPayload,
  StockTransferPayload,
  StockHistoryItem,
  LocationStock,
  VariantStockResponse,
  ProductEditInfo,
  ProductChangeLogEntry,
  AssignVariantStockPayload,
  ProductBatch,
  ProductImportResponse,
  ImportStores,
  ValuationResponse,
  StockMovementsResponse,
  ExpiryAlertItem,
  DeadStockItem,
  ProductVariantsResponse,
  CreateVariantsPayload,
} from '@/types/product';

function buildQuery(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  const qs = query.toString();
  return qs ? `?${qs}` : '';
}

export const productService = {
  list(params: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    location_id?: string;
    status?: string;
    is_active?: string;
    stock_status?: string;
    /** Filter by product type: GOODS | SERVICE. */
    product_type?: string;
    /** Filter by brand. */
    brand?: string;
    sort?: string;
    order?: string;
    /** Show variant rows alongside parents. Admin defaults to true so a
     *  product's variants stay visible and editable in the catalogue. */
    include_variants?: string;
    /** Return only the variants of this parent product. */
    parent_product_id?: string;
    /** 'true' = only parents with active variants, 'false' = only those without. */
    has_variants?: string;
  }) {
    // The API hides variants by default so the POS grid shows one card per
    // product. Admin manages them, so it opts back in unless a caller asks
    // for parents only.
    const withVariants = {
      include_variants: params.parent_product_id ? undefined : 'true',
      ...params,
    };
    return apiClientPaginated<(Product & { id: string })[]>(`/products${buildQuery(withVariants)}`);
  },

  /**
   * A parent's variants for the picker/tab: parent summary, attribute groups
   * (library-ordered) and the child rows. Active children only unless asked.
   */
  getVariants(id: string, includeInactive = false) {
    return apiClient<ProductVariantsResponse>(
      `/products/${id}/variants${buildQuery({ include_inactive: includeInactive ? 'true' : undefined })}`,
    );
  },

  /** Bulk-create variants of a parent in one transaction (all rows or none). */
  createVariants(id: string, payload: CreateVariantsPayload) {
    return apiClient<(Product & { id: string })[]>(`/products/${id}/variants`, {
      method: 'POST',
      body: payload,
    });
  },

  /** `master_id` is the master catalogue entry this store row lists (null if not linked yet). */
  getById(id: string) {
    return apiClient<Product & { id: string; master_id?: string | null }>(`/products/${id}`);
  },

  create(payload: CreateProductPayload) {
    return apiClient<Product & { id: string }>('/products', { method: 'POST', body: payload });
  },

  /**
   * Finish a draft as a create would: catalogue price, opening stock and the
   * other stores. The SKU and first store stay the draft's.
   */
  completeDraft(id: string, payload: Omit<CreateProductPayload, 'sku' | 'location_id' | 'is_draft'>) {
    return apiClient<Product & { id: string }>(`/products/${id}/complete`, { method: 'POST', body: payload });
  },

  update(id: string, payload: UpdateProductPayload) {
    return apiClient<Product & { id: string }>(`/products/${id}`, { method: 'PATCH', body: payload });
  },

  delete(id: string) {
    return apiClient<{ message: string }>(`/products/${id}`, { method: 'DELETE' });
  },

  getCategories(locationId?: string) {
    return apiClient<string[]>(`/products/categories${buildQuery({ location_id: locationId })}`);
  },

  getLowStock(params: { location_id?: string; page?: number; limit?: number }) {
    return apiClientPaginated<(Product & { id: string })[]>(`/products/low-stock${buildQuery(params)}`);
  },

  // Paginated: the endpoint returns `{ data, meta }`, and `apiClient` strips the
  // envelope down to `json.data` — so using it here handed callers the bare row
  // array while the declared type still promised `.data`. `historyData.data`
  // was therefore always undefined and the History tab rendered empty even
  // though the API returned rows.
  getStockHistory(id: string, params: { location_id?: string; page?: number; limit?: number }) {
    return apiClientPaginated<StockHistoryItem[]>(`/products/${id}/stock-history${buildQuery(params)}`);
  },

  getStock(id: string) {
    return apiClient<LocationStock[]>(`/products/${id}/stock`);
  },

  /** When the product was created and last changed, and by whom. */
  getEditInfo(id: string) {
    return apiClient<ProductEditInfo>(`/products/${id}/edit-info`);
  },

  /** Catalogue edits of the product and its variants, newest first. */
  getChangeLog(id: string, params: { page?: number; limit?: number } = {}) {
    return apiClientPaginated<ProductChangeLogEntry[]>(`/products/${id}/change-log${buildQuery(params)}`);
  },

  /** Every variant's stock per location, plus units stranded on the parent's own rows. */
  getVariantStock(id: string) {
    return apiClient<VariantStockResponse>(`/products/${id}/variant-stock`);
  },

  /** Move units from a variant parent's own row to one of its variants at the same location. */
  assignVariantStock(id: string, payload: AssignVariantStockPayload) {
    return apiClient<{ parent: { id: string; stock_quantity: number }; variant: { id: string; stock_quantity: number; cost: number } }>(
      `/products/${id}/assign-stock`,
      { method: 'POST', body: payload },
    );
  },

  getBatches(id: string) {
    return apiClient<ProductBatch[]>(`/products/${id}/batches`);
  },

  getSerials(id: string, params?: { status?: SerialStatus; location_id?: string }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.location_id) qs.set('location_id', params.location_id);
    const q = qs.toString();
    return apiClient<ProductSerial[]>(`/products/${id}/serials${q ? `?${q}` : ''}`);
  },

  adjustStock(id: string, payload: StockAdjustmentPayload) {
    // pending_approval: a write-off above the wastage limit waits in Approvals (QA IS4-002).
    return apiClient<{
      adjustment: Record<string, unknown> | null;
      pending_approval?: { wastage_log_id: string; amount: number };
      product: { id: string; stock_quantity: number; cost: number; held_quantity?: number };
    }>(
      `/products/${id}/stock`,
      { method: 'POST', body: payload },
    );
  },

  transferStock(id: string, payload: StockTransferPayload) {
    return apiClient<{ message: string }>(`/products/${id}/transfer`, { method: 'POST', body: payload });
  },

  /**
   * Import into the product masters and add them to `stores` (ids, or 'ALL' the user may use).
   * A row whose SKU already exists is skipped unless `updateExisting` is set (OD7).
   */
  importCsv(file: File, stores: ImportStores, dryRun = false, updateExisting = false) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('stores', stores === 'ALL' ? 'ALL' : stores.join(','));
    if (dryRun) formData.append('dry_run', 'true');
    if (updateExisting) formData.append('update_existing', 'true');
    // Use fetch directly for multipart form data
    return fetch('/api/v1/products/import', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(async (res) => {
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message ?? 'Import failed');
      return json.data as ProductImportResponse;
    });
  },

  getImportStatus(jobId: string) {
    return apiClient<ProductImportResponse>(`/products/import/${jobId}`);
  },

  getByBarcode(barcode: string) {
    return apiClient<Product & { id: string }>(`/products/barcode/${barcode}`);
  },

  // ─── Inventory / Reports ────────────────────────────────────────────────────

  // The valuation report is `{ data, summary, meta }`. `apiClient` keeps only
  // `data`, which silently dropped `summary` and zeroed every valuation KPI —
  // the envelope helper returns the whole body.
  getValuation(params: { location_id?: string; page?: number; limit?: number; category?: string }) {
    return apiClientEnvelope<ValuationResponse>(`/products/reports/valuation${buildQuery(params)}`);
  },

  getStockMovements(params: { location_id?: string; start_date?: string; end_date?: string }) {
    return apiClient<StockMovementsResponse>(`/products/reports/movements${buildQuery(params)}`);
  },

  // Paginated `{ data, meta }` — same envelope-unwrap trap as getStockHistory.
  getExpiryAlerts(params: { location_id?: string; window_days?: number; page?: number; limit?: number }) {
    return apiClientPaginated<ExpiryAlertItem[]>(`/products/expiry-alerts${buildQuery(params)}`);
  },

  // Plain array (no meta) — one row per active, in-stock product with no completed sale in `days`.
  getDeadStock(params: { location_id?: string; days?: number }) {
    return apiClient<DeadStockItem[]>(`/products/reports/dead-stock${buildQuery(params)}`);
  },

  getReorderReport(params: { location_id?: string; lead_time_days?: number }) {
    return apiClientPaginated<(Product & { id: string })[]>(`/products/reports/reorder${buildQuery(params)}`);
  },
};
